import isSameWeek from 'date-fns/isSameWeek'
import { formatISO } from 'date-fns'
import { DAY_MILLISECONDS, WEEK_MILLISECONDS } from '~/constants'
import { EHealthProceduresTypes, IHealthProcedures, IHealthProceduresConduct } from '~/interfaces'
import { TimelinePeriodTypes } from '~/interfaces/timeline'

export function getTimelineTotalSpaceByMode(mode: TimelinePeriodTypes): number {
  if (mode === 'days') return 24
  if (mode === 'weekly') return 6
  if (mode === 'months') return 31
  if (mode === 'years') return 12
  return 0 // only to return any number
}

export const isDatesInTheSamePeriod = (conduct: IHealthProcedures, mode: TimelinePeriodTypes): boolean => {
  const startDate = new Date(conduct.startDate)
  const endDate = new Date(conduct.endDate)

  if (mode === 'years') {
    return hasEqualYears(startDate, endDate)
  }
  if (mode === 'months') {
    return hasEqualMonths(startDate, endDate)
  }
  if (mode === 'weekly') {
    return hasEqualWeeks(startDate, endDate)
  }
  if (mode === 'days') {
    return hasEqualDays(startDate, endDate)
  }

  return false
}

export const repeatConductInRows = (
  conduct: IHealthProceduresConduct,
  mode: TimelinePeriodTypes
): IHealthProceduresConduct[] => {
  let startDate = new Date(conduct.startDate)
  const endDate = new Date(conduct.endDate)
  const conducts: IHealthProceduresConduct[] = []

  if (mode === 'years') {
    while (startDate.getTime() <= endDate.getTime()) {
      const rowStartDate = hasEqualYears(startDate, new Date(conduct.startDate))
        ? new Date(startDate)
        : new Date(startDate.getFullYear(), 0, 1)
      const rowEndDate = hasEqualYears(startDate, new Date(conduct.endDate))
        ? new Date(endDate)
        : new Date(startDate.getFullYear(), 11, 31)

      conducts.push({
        ...conduct,
        startDate: formatISO(rowStartDate),
        endDate: formatISO(rowEndDate)
      })
      startDate.setFullYear(startDate.getFullYear() + 1)
    }
  }

  if (mode === 'months') {
    while (startDate.getTime() < endDate.getTime()) {
      const rowEndDate = hasEqualMonths(startDate, endDate)
        ? endDate
        : new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0)

      conducts.push({
        ...conduct,
        startDate: formatISO(startDate),
        endDate: formatISO(rowEndDate)
      })

      if (hasEqualMonths(startDate, endDate)) {
        startDate = endDate
      } else {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1)
      }
    }
  }

  if (mode === 'weekly') {
    while (startDate.getTime() < endDate.getTime()) {
      const rowEndDate = hasEqualWeeks(startDate, endDate)
        ? endDate
        : new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6 - startDate.getDay())

      conducts.push({
        ...conduct,
        startDate: formatISO(startDate),
        endDate: formatISO(rowEndDate)
      })
      if (hasEqualWeeks(startDate, endDate)) {
        startDate = endDate
      } else {
        startDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + 7 - startDate.getDay()
        )
      }
    }
  }

  if (mode === 'days') {
    while (startDate.getTime() <= endDate.getTime()) {
      const rowStartDate = hasEqualDays(startDate, new Date(conduct.startDate))
        ? new Date(startDate)
        : new Date(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).setHours(0, 0, 0, 0))
      const rowEndDate = hasEqualDays(startDate, new Date(conduct.endDate))
        ? new Date(endDate)
        : new Date(
            new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() - 1).setHours(23, 59, 59, 999)
          )

      conducts.push({
        ...conduct,
        startDate: formatISO(rowStartDate),
        endDate: formatISO(rowEndDate)
      })
      startDate.setDate(startDate.getDate() + 1)
    }
  }

  return conducts
}

export const calculatePercentagePositionInARow = (startDate: string, mode: TimelinePeriodTypes): number => {
  const startDateFormat = new Date(startDate)
  const totalSpace = getTimelineTotalSpaceByMode(mode)

  if (mode === 'years') {
    const dateMonth = Number(startDateFormat.getMonth())
    const compensateForArrayIndex = dateMonth > 0 ? dateMonth + 1 : dateMonth
    return Math.floor((compensateForArrayIndex * 100) / totalSpace)
  }

  if (mode === 'months') {
    const startDateInDate = new Date(startDate)
    const totalDaysInMonth = new Date(startDateInDate.getFullYear(), startDateInDate.getMonth() + 1, 0).getDate()
    const dateDay = Number(startDateInDate.getDate())
    const compensateForArrayIndex = dateDay === 1 ? 0 : dateDay
    return Math.floor((compensateForArrayIndex * 100) / totalDaysInMonth)
  }

  if (mode === 'weekly') {
    const startDateWeek = startDateFormat.getDay()
    return Math.floor((startDateWeek * 100) / (totalSpace + 1))
  }

  if (mode === 'days') {
    const startDateHour = new Date(startDate).getHours()
    const startDateMinutes = new Date(startDate).getMinutes() / 60
    const roundedStartDate = Math.round(startDateHour + startDateMinutes)
    return Math.floor((roundedStartDate * 100) / totalSpace)
  }

  return 0
}

export const hasEqualYears = (startDate: Date, endDate: Date): boolean => {
  return startDate.getFullYear() === endDate.getFullYear()
}

export const hasEqualMonths = (startDate: Date, endDate: Date): boolean => {
  const firstDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
  const secondDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
  return firstDate.getTime() === secondDate.getTime()
}

export const hasEqualWeeks = (startDate: Date, endDate: Date): boolean => {
  return isSameWeek(startDate, endDate)
}

export const hasEqualDays = (startDate: Date, endDate: Date): boolean => {
  const firstDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const secondDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
  return firstDate.getTime() === secondDate.getTime()
}

export const quantityOfDaysBetweenTwoDates = (startDate: Date, endDate: Date): number => {
  const startDateWithoutHours = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const endDateWithoutHours = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
  return Math.floor((endDateWithoutHours.getTime() - startDateWithoutHours.getTime()) / DAY_MILLISECONDS)
}

export const quantityOfMonthsBetweenTwoDates = (startDate: Date, endDate: Date): number => {
  return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth())
}

export const getWeekDifferenceBetweenTwoDates = (firstDate: Date, secondDate: Date): number => {
  const differenceBetweenDates = Math.abs(firstDate.getTime() - secondDate.getTime())
  const roundInWeeksQuantity = Math.floor(differenceBetweenDates / WEEK_MILLISECONDS)
  return roundInWeeksQuantity
}

export const getPregnancyTrimester = (gestationWeeks: number): number => {
  const absoluteGestationWeeks = Math.abs(gestationWeeks)
  if (absoluteGestationWeeks <= 12) return 1
  if (absoluteGestationWeeks <= 26) return 2
  return 3
}

export const getMonthName = (date: Date | string): string => {
  const dateToFormat = typeof date === 'string' ? new Date(date) : date

  return dateToFormat.toLocaleDateString('pt-BR', {
    month: 'long',
    timeZone: 'UTC'
  })
}

export const formatDateToGroupBy = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'long',
    timeZone: 'America/Sao_Paulo'
  })
    .format(date)
    .replace(' ', ' ')
}

export const isHealthProcedurePartOfImagingTestGroup = (healthProcedure: IHealthProcedures): boolean => {
  const imagingTestGroup: string[] = [
    EHealthProceduresTypes.IMAGING_TEST,
    EHealthProceduresTypes.RADIOLOGY_IMAGING_TEST,
    EHealthProceduresTypes.MATERNAL_INFANT_IMAGING_TEST,
    EHealthProceduresTypes.ECHOCARDIOGRAM_TEST
  ]
  return imagingTestGroup.includes(healthProcedure.type)
}

export const sanitizeHealthProceduresType = (healthProcedure: IHealthProcedures): string => {
  if (isHealthProcedurePartOfImagingTestGroup(healthProcedure)) return EHealthProceduresTypes.IMAGING_TEST
  else return healthProcedure.type
}
